import {
  getDatadogInstance,
  setAnalyticsUserProperties,
  useAmplitudeAnalytics,
} from '@cw-elements/analytics-provider';
import { Grid } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'src/auth';
import { useFarm, useOrganization } from 'src/pages/organization';

type TrackingHookProps = {
  track: (
    action: string,
    additionalProperties?: Record<string, string | boolean>,
  ) => void;
};

const useTracking = (): TrackingHookProps => {
  const track = useAmplitudeAnalytics();

  return {
    track,
  };
};

const useTrackingEnrichProperties = () => {
  const { currentOrganization, currentWorkspaceId, plan } = useOrganization();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const { currentFarm } = useFarm();
  const breakpoint = Grid.useBreakpoint();
  const isMobile = !breakpoint.md;

  // The provider only forwards these once consent is granted, and it accepts string
  // values only — hence `mobileScreen` being stringified. Deliberately free of direct
  // identifiers (name, email): a single call feeds both Amplitude and Datadog RUM, so
  // anything added here reaches Amplitude too.
  const defaultProperties = useMemo(() => {
    return {
      cropwiseUserId: user?.id ?? '',
      organizationId: currentOrganization?.id ?? '',
      organizationName: currentOrganization?.name ?? '',
      workspaceId: currentWorkspaceId ?? '',
      workspaceName: currentOrganization?.workspace_name ?? '',
      farmId: currentFarm?.externalId ?? '',
      farmName: currentFarm?.name ?? '',
      jobTitle: user?.job_title ?? '',
      role: user?.role ?? '',
      planId: plan?.id ?? '',
      planName: plan?.name ?? '',
      userLanguage: user?.locale ?? '',
      mobileScreen: String(isMobile),
      interfaceLanguage: i18n.language,
    };
  }, [
    currentFarm?.externalId,
    currentFarm?.name,
    currentOrganization?.id,
    currentOrganization?.name,
    currentOrganization?.workspace_name,
    currentWorkspaceId,
    user?.job_title,
    user?.role,
    user?.id,
    user?.locale,
    plan?.id,
    plan?.name,
    i18n.language,
    isMobile,
  ]);

  useEffect(() => {
    getDatadogInstance()?.setGlobalContextProperty('organization_info', {
      id: currentOrganization?.id ?? '',
      name: currentOrganization?.name ?? '',
      workspace_name: currentOrganization?.workspace_name ?? '',
    });
  }, [
    currentOrganization?.id,
    currentOrganization?.name,
    currentOrganization?.workspace_name,
  ]);

  useEffect(() => {
    getDatadogInstance()?.setGlobalContextProperty('farm_info', {
      id: currentFarm?.externalId ?? '',
      name: currentFarm?.name ?? '',
    });
  }, [currentFarm?.externalId, currentFarm?.name]);

  useEffect(() => {
    setAnalyticsUserProperties(defaultProperties);
  }, [defaultProperties]);
};

export { useTracking, useTrackingEnrichProperties };
